<template>
	<div class="session-back-btn-wrapper">
		<v-btn text small class="session-back-btn" @click="goBack" aria-label="Retour à la liste des sessions">
			<v-icon left size="18px">mdi-arrow-left</v-icon>
			<span class="session-back-btn__label">Retour</span>
		</v-btn>
	</div>
</template>

<script>
/**
 * SessionBackButton
 *
 * Displayed at the top of SessionDetailPage, allows the user to navigate back
 * to the chapter session list (Topic Dashboard Chapter Detail) if the chapter
 * can be resolved from formationProgress. Falls back to Topic Dashboard when
 * the session cannot be matched (e.g. user arrived via email deep-link with no
 * prior navigation history).
 *
 * @prop {Object} formationProgress - The full formationProgress object from Vuex.
 *   Must contain config.parcours_list, config.topic_id.slug and formation._id.
 */
export default {
	name: 'SessionBackButton',

	props: {
		/** @type {import('vue').PropType<Object>} */
		formationProgress: {
			type: Object,
			required: true,
		},
	},

	computed: {
		/** The topic slug used in every Topic Dashboard route. */
		topicSlug() {
			return this.formationProgress.config.topic_id.slug;
		},

		/** The formation _id used as :formationId in routes. */
		formationId() {
			return this.formationProgress.formation._id;
		},

		/**
		 * Scans all parcours → categories → sessions to find which chapter
		 * the current session belongs to.
		 *
		 * Returns { idParcours, idCategory } when found, null otherwise.
		 * Null happens when the user arrived via email link and the session
		 * is not yet present in the loaded formationProgress snapshot.
		 */
		chapterLocation() {
			const sessionId = this.$route.params.sessionId;

			for (const parcoursItem of this.formationProgress.config.parcours_list) {
				const parcours = parcoursItem.parcours_id;
				for (const category of parcours.categories) {
					const found = category.sessions && category.sessions.some((s) => s._id === sessionId);
					if (found) {
						return { idParcours: parcours._id, idCategory: category._id };
					}
				}
			}

			return null;
		},
	},

	methods: {
		goBack() {
			if (this.chapterLocation) {
				const { idParcours, idCategory } = this.chapterLocation;
				this.$router.push({
					name: 'Topic Dashboard Chapter Detail',
					params: {
						topicSlug: this.topicSlug,
						formationId: this.formationId,
						idParcours,
						idCategory,
					},
				});
			} else {
				// Fallback: user arrived via email deep-link — go to topic dashboard
				this.$router.push({
					name: 'Topic Dashboard Index',
					params: {
						topicSlug: this.topicSlug,
						formationId: this.formationId,
					},
				});
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.session-back-btn {
	color: #989fa9 !important; // ortho-text-gray
	font-weight: 500;
	letter-spacing: 0.3px;
	text-transform: none;
	padding-left: 4px;

	&:hover {
		color: #6b7280 !important;
	}

	&__label {
		font-size: 0.875rem;
	}
}

@media screen and (max-width: 600px) {
	.session-back-btn__label {
		font-size: 0.813rem;
	}
}
</style>
